import React, { useState, useEffect, useCallback } from 'react';
import { rolesApi } from '../../utils/adminApi';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './Admin.css';

const PERM_GROUPS = [
  {
    label: 'Administration',
    icon: 'fas fa-shield-alt',
    codes: ['users.view', 'users.manage', 'roles.view', 'roles.manage', 'audit.view', 'settings.manage'],
  },
  {
    label: 'Organization',
    icon: 'fas fa-building',
    codes: ['entities.view', 'entities.manage', 'currencies.view', 'currencies.manage', 'periods.view', 'periods.manage', 'periods.close'],
  },
  {
    label: 'Chart of Accounts',
    icon: 'fas fa-book',
    codes: ['accounts.view', 'accounts.manage'],
  },
  {
    label: 'Vouchers',
    icon: 'fas fa-file-invoice-dollar',
    codes: ['vouchers.view', 'vouchers.create', 'vouchers.update', 'vouchers.post', 'vouchers.reverse'],
  },
  {
    label: 'Reports',
    icon: 'fas fa-chart-bar',
    codes: ['reports.view'],
  },
];

function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div className="adm-modal-overlay">
      <div className="adm-modal" style={{ maxWidth: 400 }}>
        <div className="adm-modal-head"><h2>Confirm Delete</h2></div>
        <div className="adm-modal-body adm-confirm-body">{message}</div>
        <div className="adm-modal-foot">
          <button className="adm-btn adm-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="adm-btn adm-btn--danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

function RoleModal({ role, allPermissions, onClose }) {
  const isEdit = !!role;
  const [form, setForm] = useState({
    code: role?.code || '',
    name: role?.name || '',
    description: role?.description || '',
    permissionCodes: (role?.permissions || []).map((p) => p.code),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const togglePerm = (code) =>
    setForm((f) => ({
      ...f,
      permissionCodes: f.permissionCodes.includes(code)
        ? f.permissionCodes.filter((c) => c !== code)
        : [...f.permissionCodes, code],
    }));

  const toggleGroup = (codes) => {
    const allOn = codes.every((c) => form.permissionCodes.includes(c));
    setForm((f) => ({
      ...f,
      permissionCodes: allOn
        ? f.permissionCodes.filter((c) => !codes.includes(c))
        : [...new Set([...f.permissionCodes, ...codes])],
    }));
  };

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      if (isEdit) {
        await rolesApi.update(role.id, { name: form.name, description: form.description || null, permissionCodes: form.permissionCodes });
      } else {
        await rolesApi.create({ code: form.code, name: form.name, description: form.description || null, permissionCodes: form.permissionCodes });
      }
      onClose(true);
    } catch (e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const permDescMap = {};
  allPermissions.forEach((p) => { permDescMap[p.code] = p.description; });

  return (
    <div className="adm-modal-overlay">
      <div className="adm-modal adm-modal--wide">
        <div className="adm-modal-head">
          <h2>{isEdit ? `Edit — ${role.name}` : 'Create Role'}</h2>
          <button className="adm-modal-close" onClick={() => onClose(false)}><i className="fas fa-times"></i></button>
        </div>
        <div className="adm-modal-body">
          {error && <div className="adm-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
          {!isEdit && (
            <div className="adm-field">
              <label className="adm-label">Role Code *</label>
              <input className="adm-input" value={form.code} onChange={(e) => set('code', e.target.value)}
                placeholder="e.g. accountant" autoFocus />
              <p className="adm-field-hint">Lowercase letters, digits, dash, underscore. Cannot be changed later.</p>
            </div>
          )}
          <div className="adm-field">
            <label className="adm-label">Name *</label>
            <input className="adm-input" value={form.name} onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Accountant" autoFocus={isEdit} />
          </div>
          <div className="adm-field">
            <label className="adm-label">Description</label>
            <textarea className="adm-textarea" value={form.description}
              onChange={(e) => set('description', e.target.value)} placeholder="Optional" />
          </div>

          <div className="adm-field">
            <label className="adm-label">Permissions</label>
            {PERM_GROUPS.map((g) => {
              const groupCodes = g.codes.filter((c) => permDescMap[c] !== undefined || true);
              const allOn = groupCodes.every((c) => form.permissionCodes.includes(c));
              const someOn = groupCodes.some((c) => form.permissionCodes.includes(c));
              return (
                <div key={g.label} className="adm-perm-group">
                  <div className="adm-perm-group-label" style={{ cursor: 'pointer' }} onClick={() => toggleGroup(groupCodes)}>
                    <i className={g.icon}></i>
                    {g.label}
                    <span style={{ marginLeft: 'auto', fontSize: 'var(--font-size-xs)', color: someOn ? 'var(--primary)' : 'var(--text-faint)' }}>
                      {form.permissionCodes.filter((c) => groupCodes.includes(c)).length}/{groupCodes.length}
                    </span>
                    <input type="checkbox" checked={allOn} onChange={() => toggleGroup(groupCodes)}
                      ref={(el) => { if (el) el.indeterminate = someOn && !allOn; }} />
                  </div>
                  <div className="adm-perm-list">
                    {groupCodes.map((code) => (
                      <label key={code} className="adm-perm-item">
                        <input type="checkbox" checked={form.permissionCodes.includes(code)} onChange={() => togglePerm(code)} />
                        <span className="adm-perm-code">{code}</span>
                        {permDescMap[code] && (
                          <span style={{ color: 'var(--text-faint)', fontSize: 'var(--font-size-xs)', marginLeft: 2 }}>
                            — {permDescMap[code]}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="adm-modal-foot">
          <span style={{ flex: 1, fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            {form.permissionCodes.length} permission{form.permissionCodes.length !== 1 ? 's' : ''} selected
          </span>
          <button className="adm-btn adm-btn--ghost" onClick={() => onClose(false)}>Cancel</button>
          <button className="adm-btn adm-btn--primary" onClick={submit} disabled={saving}>
            {saving ? <LoadingSpinner size="small" variant="button" /> : (isEdit ? 'Save Changes' : 'Create Role')}
          </button>
        </div>
      </div>
    </div>
  );
}

const RolesPage = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('roles.manage');

  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [r, p] = await Promise.all([rolesApi.list(), rolesApi.listPermissions()]);
      setRoles(r);
      setAllPermissions(p);
    } catch (e) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeModal = (refresh) => { setModal(null); if (refresh) load(); };

  const doDelete = async () => {
    try {
      await rolesApi.remove(modal.role.id);
      closeModal(true);
    } catch (e) { setError(e.message || 'Delete failed'); setModal(null); }
  };

  return (
    <div className="adm-page">
      <div className="adm-header">
        <div>
          <h1 className="adm-title"><i className="fas fa-user-shield" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Roles & Permissions</h1>
          <p className="adm-subtitle">Manage roles and their permission sets.</p>
        </div>
        {canManage && (
          <button className="adm-btn adm-btn--primary" onClick={() => setModal({ type: 'create' })}>
            <i className="fas fa-plus"></i> New Role
          </button>
        )}
      </div>

      {error && <div className="adm-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}

      <div className="adm-card">
        {loading ? (
          <LoadingSpinner variant="default" message="Loading roles…" />
        ) : (
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Permissions</th>
                  <th>Type</th>
                  {canManage && <th style={{ textAlign: 'right' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {roles.length === 0 && (
                  <tr><td colSpan={canManage ? 6 : 5} className="adm-empty">No roles found.</td></tr>
                )}
                {roles.map((r) => (
                  <tr key={r.id}>
                    <td><code style={{ fontFamily: 'var(--font-family-mono)', fontSize: '0.82rem' }}>{r.code}</code></td>
                    <td><strong>{r.name}</strong></td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{r.description || '—'}</td>
                    <td>
                      <span className="adm-badge adm-badge--system">{(r.permissions || []).length} permission{(r.permissions || []).length !== 1 ? 's' : ''}</span>
                    </td>
                    <td>
                      {r.isSystem
                        ? <span className="adm-badge adm-badge--base">System</span>
                        : <span className="adm-badge" style={{ background: 'var(--surface-3)', color: 'var(--text-muted)' }}>Custom</span>}
                    </td>
                    {canManage && (
                      <td>
                        <div className="adm-actions">
                          <button className="adm-btn adm-btn--ghost adm-btn--sm" title="Edit"
                            onClick={() => setModal({ type: 'edit', role: r })}>
                            <i className="fas fa-pen"></i>
                          </button>
                          {!r.isSystem && (
                            <button className="adm-btn adm-btn--danger adm-btn--sm" title="Delete"
                              onClick={() => setModal({ type: 'delete', role: r })}>
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal?.type === 'create' && <RoleModal allPermissions={allPermissions} onClose={closeModal} />}
      {modal?.type === 'edit' && <RoleModal role={modal.role} allPermissions={allPermissions} onClose={closeModal} />}
      {modal?.type === 'delete' && (
        <ConfirmModal
          message={<>Delete role <strong>{modal.role.name}</strong>? Users assigned this role will lose its permissions.</>}
          onConfirm={doDelete}
          onCancel={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default RolesPage;
